/**
 * ClawPowers Agent — Skills Catalog
 *
 * Static catalog of all skills available to ClawPowers.
 * Sources:
 *   - openclaw-bundled: 51 skills bundled with OpenClaw
 *   - managed: skills installed in ~/.openclaw/skills/
 *
 * Generated by reading SKILL.md frontmatter from each skill directory.
 * This is a STATIC array so it works everywhere (no runtime file I/O).
 */

// ─── Types ────────────────────────────────────────────────────────────────────

export interface SkillEntry {
  name: string;
  description: string;
  source: 'openclaw-bundled' | 'clawpowers' | 'managed';
  category: string;
}

// ─── OpenClaw Bundled Skills (51) ─────────────────────────────────────────────

export const SKILLS_CATALOG: SkillEntry[] = [
  // ── productivity ──────────────────────────────────────────────────────────
  {
    name: '1password',
    description: 'Set up and use 1Password CLI (op). Use when installing the CLI, enabling desktop app integration, signing in (single or multi-account), or reading/injecting/running secrets via op.',
    source: 'openclaw-bundled',
    category: 'productivity',
  },
  {
    name: 'apple-notes',
    description: 'Manage Apple Notes via the `memo` CLI on macOS (create, view, edit, delete, search, move, and export notes). Use when a user asks OpenClaw to add a note, list notes, search notes, or manage note folders.',
    source: 'openclaw-bundled',
    category: 'productivity',
  },
  {
    name: 'apple-reminders',
    description: 'Manage Apple Reminders via remindctl CLI (list, add, edit, complete, delete). Supports lists, date filters, and JSON/plain output.',
    source: 'openclaw-bundled',
    category: 'productivity',
  },
  {
    name: 'bear-notes',
    description: 'Create, search, and manage Bear notes via grizzly CLI.',
    source: 'openclaw-bundled',
    category: 'productivity',
  },
  {
    name: 'notion',
    description: 'Notion API for creating and managing pages, databases, and blocks.',
    source: 'openclaw-bundled',
    category: 'productivity',
  },
  {
    name: 'obsidian',
    description: 'Work with Obsidian vaults (plain Markdown notes) and automate via obsidian-cli.',
    source: 'openclaw-bundled',
    category: 'productivity',
  },
  {
    name: 'things-mac',
    description: 'Manage Things 3 via the `things` CLI on macOS (add/update projects+todos via URL scheme; read/search/list from the local Things database). Use when a user asks OpenClaw to add a task to Things, list items, or search tasks.',
    source: 'openclaw-bundled',
    category: 'productivity',
  },
  {
    name: 'trello',
    description: 'Manage Trello boards, lists, and cards via the Trello REST API.',
    source: 'openclaw-bundled',
    category: 'productivity',
  },
  {
    name: 'summarize',
    description: 'Summarize or extract text/transcripts from URLs, podcasts, and local files (great fallback for "transcribe this YouTube/video").',
    source: 'openclaw-bundled',
    category: 'productivity',
  },
  {
    name: 'tmux',
    description: 'Remote-control tmux sessions for interactive CLIs by sending keystrokes and scraping pane output.',
    source: 'openclaw-bundled',
    category: 'productivity',
  },
  // ── development ───────────────────────────────────────────────────────────
  {
    name: 'coding-agent',
    description: 'Delegate coding tasks to Codex, Claude Code, or Pi agents via background process. Use when: (1) building/creating new features or apps, (2) reviewing PRs (spawn in temp dir), (3) refactoring large codebases.',
    source: 'openclaw-bundled',
    category: 'development',
  },
  {
    name: 'github',
    description: 'GitHub operations via `gh` CLI: issues, PRs, CI runs, code review, API queries. Use when: (1) checking PR status or CI, (2) creating/commenting on issues, (3) listing/filtering PRs or issues, (4) viewing diffs.',
    source: 'openclaw-bundled',
    category: 'development',
  },
  {
    name: 'gh-issues',
    description: 'Fetch GitHub issues, spawn sub-agents to implement fixes and open PRs, then monitor and address PR review comments.',
    source: 'openclaw-bundled',
    category: 'development',
  },
  {
    name: 'skill-creator',
    description: 'Create, edit, improve, or audit AgentSkills. Use when creating a new skill from scratch or when asked to improve, review, audit, tidy up, or clean up an existing skill or SKILL.md file.',
    source: 'openclaw-bundled',
    category: 'development',
  },
  {
    name: 'clawhub',
    description: 'Use the ClawHub CLI to search, install, update, and publish agent skills from clawhub.com. Use when you need to fetch new skills on the fly, sync installed skills to latest or a specific version, or publish your own skills.',
    source: 'openclaw-bundled',
    category: 'development',
  },
  {
    name: 'oracle',
    description: 'Best practices for using the oracle CLI (prompt + file bundling, engines, sessions, and file attachment patterns).',
    source: 'openclaw-bundled',
    category: 'development',
  },
  {
    name: 'mcporter',
    description: 'Use the mcporter CLI to list, configure, auth, and call MCP servers/tools directly (HTTP or stdio), including ad-hoc servers, config edits, and CLI/type generation.',
    source: 'openclaw-bundled',
    category: 'development',
  },
  {
    name: 'nano-pdf',
    description: 'Edit PDFs with natural-language instructions using the nano-pdf CLI.',
    source: 'openclaw-bundled',
    category: 'development',
  },
  {
    name: 'node-connect',
    description: 'Diagnose OpenClaw node connection and pairing failures for Android, iOS, and macOS companion apps. Use when QR/setup code/manual connect fails, local Wi-Fi works but VPS/tailnet does not, or errors mention pairing.',
    source: 'openclaw-bundled',
    category: 'development',
  },
  // ── communication ─────────────────────────────────────────────────────────
  {
    name: 'discord',
    description: 'Discord ops via the message tool (channel=discord).',
    source: 'openclaw-bundled',
    category: 'communication',
  },
  {
    name: 'slack',
    description: 'Use when you need to control Slack from OpenClaw via the slack tool, including reacting to messages or pinning/unpinning items in Slack channels or DMs.',
    source: 'openclaw-bundled',
    category: 'communication',
  },
  {
    name: 'bluebubbles',
    description: 'Use when you need to send or manage iMessages via BlueBubbles (recommended iMessage integration). Calls go through the generic message tool with channel=bluebubbles.',
    source: 'openclaw-bundled',
    category: 'communication',
  },
  {
    name: 'imsg',
    description: 'iMessage/SMS CLI for listing chats, history, and sending messages via Messages.app.',
    source: 'openclaw-bundled',
    category: 'communication',
  },
  {
    name: 'wacli',
    description: 'Send WhatsApp messages to other people or search/sync WhatsApp history via the wacli CLI (not for normal user chats).',
    source: 'openclaw-bundled',
    category: 'communication',
  },
  {
    name: 'himalaya',
    description: 'CLI to manage emails via IMAP/SMTP. Use `himalaya` to list, read, write, reply, forward, search, and organize emails from the terminal. Supports multiple accounts and message composition with MML.',
    source: 'openclaw-bundled',
    category: 'communication',
  },
  {
    name: 'gog',
    description: 'Google Workspace CLI for Gmail, Calendar, Drive, Contacts, Sheets, and Docs.',
    source: 'openclaw-bundled',
    category: 'communication',
  },
  // ── media ─────────────────────────────────────────────────────────────────
  {
    name: 'camsnap',
    description: 'Capture frames or clips from RTSP/ONVIF cameras.',
    source: 'openclaw-bundled',
    category: 'media',
  },
  {
    name: 'gifgrep',
    description: 'Search GIF providers with CLI/TUI, download results, and extract stills/sheets.',
    source: 'openclaw-bundled',
    category: 'media',
  },
  {
    name: 'video-frames',
    description: 'Extract frames or short clips from videos using ffmpeg.',
    source: 'openclaw-bundled',
    category: 'media',
  },
  {
    name: 'openai-whisper',
    description: 'Local speech-to-text with the Whisper CLI (no API key).',
    source: 'openclaw-bundled',
    category: 'media',
  },
  {
    name: 'openai-whisper-api',
    description: 'Transcribe audio via OpenAI Audio Transcriptions API (Whisper).',
    source: 'openclaw-bundled',
    category: 'media',
  },
  {
    name: 'sag',
    description: 'ElevenLabs text-to-speech with mac-style say UX.',
    source: 'openclaw-bundled',
    category: 'media',
  },
  {
    name: 'sherpa-onnx-tts',
    description: 'Local text-to-speech via sherpa-onnx (offline, no cloud).',
    source: 'openclaw-bundled',
    category: 'media',
  },
  {
    name: 'songsee',
    description: 'Generate spectrograms and feature-panel visualizations from audio with the songsee CLI.',
    source: 'openclaw-bundled',
    category: 'media',
  },
  {
    name: 'voice-call',
    description: 'Start voice calls via the OpenClaw voice-call plugin.',
    source: 'openclaw-bundled',
    category: 'media',
  },
  {
    name: 'peekaboo',
    description: 'Capture and automate macOS UI with the Peekaboo CLI.',
    source: 'openclaw-bundled',
    category: 'media',
  },
  // ── music ─────────────────────────────────────────────────────────────────
  {
    name: 'spotify-player',
    description: 'Terminal Spotify playback/search via spogo (preferred) or spotify_player.',
    source: 'openclaw-bundled',
    category: 'music',
  },
  {
    name: 'sonoscli',
    description: 'Control Sonos speakers (discover/status/play/volume/group).',
    source: 'openclaw-bundled',
    category: 'music',
  },
  {
    name: 'blucli',
    description: 'BluOS CLI (blu) for discovery, playback, grouping, and volume.',
    source: 'openclaw-bundled',
    category: 'music',
  },
  // ── smart-home ────────────────────────────────────────────────────────────
  {
    name: 'openhue',
    description: 'Control Philips Hue lights and scenes via the OpenHue CLI.',
    source: 'openclaw-bundled',
    category: 'smart-home',
  },
  {
    name: 'eightctl',
    description: 'Control Eight Sleep pods (status, temperature, alarms, schedules).',
    source: 'openclaw-bundled',
    category: 'smart-home',
  },
  // ── ai ────────────────────────────────────────────────────────────────────
  {
    name: 'gemini',
    description: 'Gemini CLI for one-shot Q&A, summaries, and generation.',
    source: 'openclaw-bundled',
    category: 'ai',
  },
  {
    name: 'canvas',
    description: 'Display HTML content on connected OpenClaw nodes (Mac app, iOS, Android). Great for displaying games, visualizations, and dashboards.',
    source: 'openclaw-bundled',
    category: 'ai',
  },
  // ── utilities ─────────────────────────────────────────────────────────────
  {
    name: 'blogwatcher',
    description: 'Monitor blogs and RSS/Atom feeds for updates using the blogwatcher CLI.',
    source: 'openclaw-bundled',
    category: 'utilities',
  },
  {
    name: 'goplaces',
    description: 'Query Google Places API (New) via the goplaces CLI for text search, place details, resolve, and reviews. Use for human-friendly place lookup or JSON output for scripts.',
    source: 'openclaw-bundled',
    category: 'utilities',
  },
  {
    name: 'healthcheck',
    description: 'Host security hardening and risk-tolerance configuration for OpenClaw deployments. Use when a user asks for security audits, firewall/SSH/update hardening, risk posture, exposure review, or OpenClaw cron health.',
    source: 'openclaw-bundled',
    category: 'utilities',
  },
  {
    name: 'model-usage',
    description: 'Use CodexBar CLI local cost usage to summarize per-model usage for Codex or Claude, including the current (most recent) model or a full model breakdown. Trigger when asked for model-level usage/cost details.',
    source: 'openclaw-bundled',
    category: 'utilities',
  },
  {
    name: 'ordercli',
    description: 'Foodora-only CLI for checking past orders and active order status (Deliveroo WIP).',
    source: 'openclaw-bundled',
    category: 'utilities',
  },
  {
    name: 'session-logs',
    description: 'Search and analyze your own session logs (older/parent conversations) using jq.',
    source: 'openclaw-bundled',
    category: 'utilities',
  },
  {
    name: 'weather',
    description: 'Get current weather and forecasts via wttr.in or Open-Meteo. Use when: user asks about weather, temperature, or forecasts for any location. NOT for: historical weather data, severe weather alerts, or other weather APIs.',
    source: 'openclaw-bundled',
    category: 'utilities',
  },
  {
    name: 'xurl',
    description: 'A CLI tool for making authenticated requests to the X (Twitter) API. Use this skill when you need to post tweets, reply, quote, search, read posts, manage followers, send DMs, upload media, or interact with X/Twitter programmatically.',
    source: 'openclaw-bundled',
    category: 'utilities',
  },

  // ─── Managed Skills (~/.openclaw/skills/) ──────────────────────────────────
  {
    name: 'agent-nexus-2',
    description: 'Multi-agent coordination and task delegation for complex workflows requiring parallel agent execution.',
    source: 'managed',
    category: 'development',
  },
  {
    name: 'autoresearch',
    description: 'Autonomous code quality improvement loop using keep-or-revert cycles. Optimizes a composite quality score derived from tests, lint, and type coverage.',
    source: 'managed',
    category: 'development',
  },
  {
    name: 'business-strategy',
    description: 'PMF validation, beachhead identification, activity ROI analysis, kill/invest decisions, and strategic metrics for the AI Agent Economy.',
    source: 'managed',
    category: 'productivity',
  },
  {
    name: 'coding-discipline.skill',
    description: 'Enforces strict TypeScript coding standards, test-first development, and zero-stub policies for production-grade agent code.',
    source: 'managed',
    category: 'development',
  },
  {
    name: 'content-writer',
    description: 'Use this skill everytime you are writing an article, social media post, email, etc.',
    source: 'managed',
    category: 'communication',
  },
  {
    name: 'execution-validation.skill',
    description: 'Multi-round automated validation pipeline for TypeScript/Solidity projects before publish or deploy.',
    source: 'managed',
    category: 'development',
  },
  {
    name: 'humanize',
    description: 'Transforms AI-generated writing into content that reads authentically human — passing AI detectors and resonating with real readers.',
    source: 'managed',
    category: 'communication',
  },
  {
    name: 'polyclaw',
    description: 'Trade on Polymarket via split + CLOB execution. Browse markets, track positions with P&L, discover hedges via LLM. Polygon/Web3.',
    source: 'managed',
    category: 'finance',
  },
  {
    name: 'prospector',
    description: 'Find leads, prospects, and contacts matching an Ideal Customer Profile. Searches companies via Exa and enriches contacts via Apollo, outputting to CSV and optionally syncing to Attio CRM.',
    source: 'managed',
    category: 'productivity',
  },
  {
    name: 'rsi.skill',
    description: 'RSI self-improvement cycles implementing measure → hypothesize → mutate → test → apply/discard → repeat for agent capability enhancement.',
    source: 'managed',
    category: 'development',
  },
  {
    name: 'security',
    description: 'Infrastructure threat detection, vulnerability management, and security audit workflows for the OpenClaw workspace.',
    source: 'managed',
    category: 'utilities',
  },
  {
    name: 'strykr-prism',
    description: 'Real-time financial data API for AI agents. Stocks, crypto, forex, ETFs. 120+ endpoints. Alternative to Alpha Vantage, CoinGecko. Works with Claude, Cursor.',
    source: 'managed',
    category: 'finance',
  },
  {
    name: 'taskbridge',
    description: 'Bridge tasks between agent sessions, preserving context and handoff state for long-running multi-session workflows.',
    source: 'managed',
    category: 'productivity',
  },
  {
    name: 'validator-agent',
    description: 'Multi-round automated validation pipeline for TypeScript/Solidity projects. Runs 8 rounds of checks before any publish or deploy: compile gate, lint, test suite, security audit, type coverage, docs, changelog, and final review.',
    source: 'managed',
    category: 'development',
  },
  {
    name: 'webmcp-payments',
    description: 'Handle HTTP 402 Payment Required responses via agentpay-mcp, enabling autonomous micropayment execution within configured spending limits.',
    source: 'managed',
    category: 'finance',
  },
];

// ─── Count ────────────────────────────────────────────────────────────────────

export const SKILLS_COUNT = SKILLS_CATALOG.length;
